import React, { useState, useRef } from 'react';
import { useAuth } from '@repo/common';
import { useDashboardBreakpoint } from '@repo/common';
import { BaseButton } from '@repo/common';
import SidePanner from './SidePanner';
import { ReactComponent as GreenCircleIcon } from '@repo/common/assets/icons/kpathportDashboard/icon_20px_green-circle.svg';
import { ReactComponent as DashboardRoute } from '@repo/common/assets/images/dashboardRoute.svg';
import { ReactComponent as DashboardTabletRoute } from '@repo/common/assets/images/dashboardTabletRoute.svg';
import { ReactComponent as DashboardMobileRoute } from '@repo/common/assets/images/dashboardMobileRoute.svg';
import { ReactComponent as DashboardCircle } from '@repo/common/assets/images/dashboardCircle.svg';
import { ReactComponent as DashboardCircleActive } from '@repo/common/assets/images/dashboardCircleActive.svg';
import { ReactComponent as LeftFootprint } from '@repo/common/assets/images/dashboardLeftFootprint.svg';
import { ReactComponent as RightFootprint } from '@repo/common/assets/images/dashboardRightFootprint.svg';

const Landmark = ({ src, alt, width, pos, imgStyle }) => (
  <div className='absolute flex items-end justify-end' style={pos}>
    <img
      src={src}
      alt={alt}
      style={{ width, height: 'auto', flexShrink: 0, ...imgStyle }}
    />
  </div>
);

const STEP_CIRCLES = {
  desktop: [
    { step: 'step01', top: '18%', left: '30.5%' },
    { step: 'step02', top: '31.2%', left: '47.5%' },
    { step: 'step03', top: '46%', left: '52.2%' },
    { step: 'step04', top: '57.8%', left: '37%' },
    { step: 'step05', top: '79.5%', left: '34%' },
  ],
  tablet: [
    { step: 'step01', top: '5.5%', left: '34.4%' },
    { step: 'step02', top: '25%', left: '41.6%' },
    { step: 'step03', top: '48%', left: '47.6%' },
    { step: 'step04', top: '61.6%', left: '34.2%' },
    { step: 'step05', top: '83.2%', left: '38%' },
  ],
  mobile: [
    { step: 'step01', top: '5%', left: '39%' },
    { step: 'step02', top: '26%', left: '38%' },
    { step: 'step03', top: '45%', left: '55%' },
    { step: 'step04', top: '64%', left: '38%' },
    { step: 'step05', top: '83.6%', left: '32%' },
  ],
};

const STEP_LABELS = {
  desktop: [
    {
      step: 'step01',
      top: '22.2%',
      left: '27%',
      width: '137px',
      stepText: 'Step 01',
      label: 'Cultural Explorer',
      activeTop: '23.6%',
      activeLeft: '26.4%',
    },
    {
      step: 'step02',
      top: '35.4%',
      left: '44.6%',
      width: '120px',
      stepText: 'Step 02',
      label: 'Career Builder',
      activeTop: '36.6%',
      activeLeft: '44.4%',
    },
    {
      step: 'step03',
      top: '50.2%',
      left: '48.4%',
      width: '137px',
      stepText: 'Step 03',
      label: 'Koready Navigator',
      activeTop: '51.6%',
      activeLeft: '48.6%',
    },
    {
      step: 'step04',
      top: '62%',
      left: '32.4%',
      width: '156px',
      stepText: 'Step 04',
      label: 'Network Connector',
      activeTop: '63.2%',
      activeLeft: '32.2%',
    },
    {
      step: 'step05',
      top: '83.6%',
      left: '31%',
      width: '114px',
      stepText: 'Step 05',
      label: 'Be Koready',
      activeTop: '85%',
      activeLeft: '31%',
    },
  ],
  tablet: [
    {
      step: 'step01',
      top: '11.6%',
      left: '29.6%',
      width: '137px',
      stepText: 'Step 01',
      label: 'Cultural Explorer',
      activeTop: '12.8%',
      activeLeft: '28.8%',
    },
    {
      step: 'step02',
      top: '31.4%',
      left: '37.6%',
      width: '120px',
      stepText: 'Step 02',
      label: 'Career Builder',
      activeTop: '32.4%',
      activeLeft: '37%',
    },
    {
      step: 'step03',
      top: '54.6%',
      left: '43%',
      width: '137px',
      stepText: 'Step 03',
      label: 'Koready Navigator',
      activeTop: '55.8%',
      activeLeft: '42.2%',
    },
    {
      step: 'step04',
      top: '68%',
      left: '28.2%',
      width: '156px',
      stepText: 'Step 04',
      label: 'Network Connector',
      activeTop: '69%',
      activeLeft: '27.6%',
    },
    {
      step: 'step05',
      top: '89.4%',
      left: '34%',
      width: '114px',
      stepText: 'Step 05',
      label: 'Be Koready',
      activeTop: '90.4%',
      activeLeft: '34%',
    },
  ],
  mobile: [
    {
      step: 'step01',
      top: '11.6%',
      left: '27%',
      width: '129px',
      stepText: 'Step 01',
      label: 'Cultural Explorer',
      activeTop: '12.5%',
      activeLeft: '27.5%',
    },
    {
      step: 'step02',
      top: '32%',
      left: '27.2%',
      width: '114px',
      stepText: 'Step 02',
      label: 'Career Builder',
      activeTop: '33.5%',
      activeLeft: '28%',
    },
    {
      step: 'step03',
      top: '51%',
      left: '43.2%',
      width: '119px',
      stepText: 'Step 03',
      label: 'Koready Navigator',
      activeTop: '52.5%',
      activeLeft: '45%',
    },
    {
      step: 'step04',
      top: '70%',
      left: '23%',
      width: '146px',
      stepText: 'Step 04',
      label: 'Network Connector',
      activeTop: '71.5%',
      activeLeft: '22%',
    },
    {
      step: 'step05',
      top: '89.8%',
      left: '22.5%',
      width: '106px',
      stepText: 'Step 05',
      label: 'Be Koready',
      activeTop: '90.4%',
      activeLeft: '23%',
    },
  ],
};

const FOOTPRINT_DATA = {
  desktop: {
    step01: [
      {
        top: '-2%',
        left: '0.6%',
        type: 'left',
        delay: '0.8s',
        rotate: '50deg',
        color: '#4DD5A3',
      },
      {
        top: '-2.2%',
        left: '-0.4%',
        type: 'right',
        delay: '0.8s',
        rotate: '40deg',
        color: '#4DD5A3',
      },
      {
        top: '-4.8%',
        left: '1.0%',
        type: 'left',
        delay: '0.4s',
        rotate: '70deg',
        color: '#7EEAC9',
      },
      {
        top: '-4.4%',
        left: '0.1%',
        type: 'right',
        delay: '0.4s',
        rotate: '56deg',
        color: '#7EEAC9',
      },
      {
        top: '-7.2%',
        left: '1.9%',
        type: 'left',
        delay: '0s',
        rotate: '90deg',
        color: '#C6F9E9',
      },
      {
        top: '-7.3%',
        left: '0.9%',
        type: 'right',
        delay: '0s',
        rotate: '70deg',
        color: '#C6F9E9',
      },
    ],
    step02: [
      {
        top: '-1.4%',
        left: '-1.8%',
        type: 'left',
        delay: '0.8s',
        rotate: '-5deg',
        color: '#4DD5A3',
      },
      {
        top: '-1.4%',
        left: '-3%',
        type: 'right',
        delay: '0.8s',
        rotate: '-5deg',
        color: '#4DD5A3',
      },
      {
        top: '-2.8%',
        left: '-4.2%',
        type: 'left',
        delay: '0.4s',
        rotate: '-5deg',
        color: '#7EEAC9',
      },
      {
        top: '-2.8%',
        left: '-5.4%',
        type: 'right',
        delay: '0.4s',
        rotate: '-5deg',
        color: '#7EEAC9',
      },
      {
        top: '-4.4%',
        left: '-6.6%',
        type: 'left',
        delay: '0s',
        rotate: '-10deg',
        color: '#C6F9E9',
      },
      {
        top: '-4.4%',
        left: '-7.8%',
        type: 'right',
        delay: '0s',
        rotate: '-10deg',
        color: '#C6F9E9',
      },
    ],
    step03: [
      {
        top: '-1.8%',
        left: '2.8%',
        type: 'left',
        delay: '0.8s',
        rotate: '80deg',
        color: '#4DD5A3',
      },
      {
        top: '-1.8%',
        left: '1.8%',
        type: 'right',
        delay: '0.8s',
        rotate: '80deg',
        color: '#4DD5A3',
      },
      {
        top: '-4%',
        left: '3.2%',
        type: 'left',
        delay: '0.4s',
        rotate: '72deg',
        color: '#7EEAC9',
      },
      {
        top: '-4%',
        left: '2.2%',
        type: 'right',
        delay: '0.4s',
        rotate: '62deg',
        color: '#7EEAC9',
      },
      {
        top: '-6.4%',
        left: '3%',
        type: 'left',
        delay: '0s',
        rotate: '60deg',
        color: '#C6F9E9',
      },
      {
        top: '-6.4%',
        left: '2%',
        type: 'right',
        delay: '0s',
        rotate: '30deg',
        color: '#C6F9E9',
      },
    ],
    step04: [
      {
        top: '-1.4%',
        left: '4%',
        type: 'left',
        delay: '0.8s',
        rotate: '100deg',
        color: '#4DD5A3',
      },
      {
        top: '-1.6%',
        left: '3%',
        type: 'right',
        delay: '0.8s',
        rotate: '100deg',
        color: '#4DD5A3',
      },
      {
        top: '-2.9%',
        left: '6.4%',
        type: 'left',
        delay: '0.4s',
        rotate: '105deg',
        color: '#7EEAC9',
      },
      {
        top: '-3.1%',
        left: '5.4%',
        type: 'right',
        delay: '0.4s',
        rotate: '105deg',
        color: '#7EEAC9',
      },
      {
        top: '-4.6%',
        left: '8.5%',
        type: 'left',
        delay: '0s',
        rotate: '110deg',
        color: '#C6F9E9',
      },
      {
        top: '-4.8%',
        left: '7.5%',
        type: 'right',
        delay: '0s',
        rotate: '110deg',
        color: '#C6F9E9',
      },
    ],
    step05: [
      {
        top: '-1.4%',
        left: '-1.8%',
        type: 'left',
        delay: '0.8s',
        rotate: '-5deg',
        color: '#4DD5A3',
      },
      {
        top: '-1.4%',
        left: '-3%',
        type: 'right',
        delay: '0.8s',
        rotate: '-5deg',
        color: '#4DD5A3',
      },
      {
        top: '-3%',
        left: '-3.2%',
        type: 'left',
        delay: '0.4s',
        rotate: '1deg',
        color: '#7EEAC9',
      },
      {
        top: '-3%',
        left: '-4.4%',
        type: 'right',
        delay: '0.4s',
        rotate: '-12deg',
        color: '#7EEAC9',
      },
      {
        top: '-4.8%',
        left: '-4.2%',
        type: 'left',
        delay: '0s',
        rotate: '20deg',
        color: '#C6F9E9',
      },
      {
        top: '-4.8%',
        left: '-5.2%',
        type: 'right',
        delay: '0s',
        rotate: '10deg',
        color: '#C6F9E9',
      },
    ],
  },
  tablet: {
    step01: [
      {
        top: '-2%',
        left: '4.6%',
        type: 'left',
        delay: '0.8s',
        rotate: '86deg',
        color: '#4DD5A3',
      },
      {
        top: '-2%',
        left: '3.6%',
        type: 'right',
        delay: '0.8s',
        rotate: '86deg',
        color: '#4DD5A3',
      },
      {
        top: '-4.5%',
        left: '6%',
        type: 'left',
        delay: '0.4s',
        rotate: '98deg',
        color: '#7EEAC9',
      },
      {
        top: '-4.6%',
        left: '4.9%',
        type: 'right',
        delay: '0.4s',
        rotate: '90deg',
        color: '#7EEAC9',
      },
      {
        top: '-8%',
        left: '2.5%',
        type: 'left',
        delay: '0s',
        rotate: '24deg',
        color: '#C6F9E9',
      },
      {
        top: '-8%',
        left: '1.7%',
        type: 'right',
        delay: '0s',
        rotate: '24deg',
        color: '#C6F9E9',
      },
    ],
    step02: [
      {
        top: '-1.4%',
        left: '-1.7%',
        type: 'left',
        delay: '0.8s',
        rotate: '-5deg',
        color: '#4DD5A3',
      },
      {
        top: '-1.4%',
        left: '-2.9%',
        type: 'right',
        delay: '0.8s',
        rotate: '-5deg',
        color: '#4DD5A3',
      },
      {
        top: '-3.2%',
        left: '-3.4%',
        type: 'left',
        delay: '0.4s',
        rotate: '-5deg',
        color: '#7EEAC9',
      },
      {
        top: '-3.2%',
        left: '-4.6%',
        type: 'right',
        delay: '0.4s',
        rotate: '-5deg',
        color: '#7EEAC9',
      },
      {
        top: '-5.2%',
        left: '-5.4%',
        type: 'left',
        delay: '0s',
        rotate: '-9deg',
        color: '#C6F9E9',
      },
      {
        top: '-5.2%',
        left: '-6.6%',
        type: 'right',
        delay: '0s',
        rotate: '-9deg',
        color: '#C6F9E9',
      },
    ],
    step03: [
      {
        top: '-1.6%',
        left: '4.2%',
        type: 'left',
        delay: '0.8s',
        rotate: '90deg',
        color: '#4DD5A3',
      },
      {
        top: '-1.8%',
        left: '3.2%',
        type: 'right',
        delay: '0.8s',
        rotate: '90deg',
        color: '#4DD5A3',
      },
      {
        top: '-4.3%',
        left: '5.6%',
        type: 'left',
        delay: '0.4s',
        rotate: '74deg',
        color: '#7EEAC9',
      },
      {
        top: '-4.3%',
        left: '4.6%',
        type: 'right',
        delay: '0.4s',
        rotate: '74deg',
        color: '#7EEAC9',
      },
      {
        top: '-7.4%',
        left: '6.1%',
        type: 'left',
        delay: '0s',
        rotate: '50deg',
        color: '#C6F9E9',
      },
      {
        top: '-7.4%',
        left: '5.1%',
        type: 'right',
        delay: '0s',
        rotate: '50deg',
        color: '#C6F9E9',
      },
    ],
    step04: [
      {
        top: '-1.4%',
        left: '4%',
        type: 'left',
        delay: '0.8s',
        rotate: '100deg',
        color: '#4DD5A3',
      },
      {
        top: '-2%',
        left: '3%',
        type: 'right',
        delay: '0.8s',
        rotate: '100deg',
        color: '#4DD5A3',
      },
      {
        top: '-3.6%',
        left: '6.4%',
        type: 'left',
        delay: '0.4s',
        rotate: '105deg',
        color: '#7EEAC9',
      },
      {
        top: '-4.2%',
        left: '5.4%',
        type: 'right',
        delay: '0.4s',
        rotate: '105deg',
        color: '#7EEAC9',
      },
      {
        top: '-5.8%',
        left: '8.5%',
        type: 'left',
        delay: '0s',
        rotate: '110deg',
        color: '#C6F9E9',
      },
      {
        top: '-6.5%',
        left: '7.5%',
        type: 'right',
        delay: '0s',
        rotate: '110deg',
        color: '#C6F9E9',
      },
    ],
    step05: [
      {
        top: '-2.4%',
        left: '-2.2%',
        type: 'left',
        delay: '0.8s',
        rotate: '-4deg',
        color: '#4DD5A3',
      },
      {
        top: '-2.4%',
        left: '-3.4%',
        type: 'right',
        delay: '0.8s',
        rotate: '-4deg',
        color: '#4DD5A3',
      },
      {
        top: '-4.6%',
        left: '-3.9%',
        type: 'left',
        delay: '0.4s',
        rotate: '2deg',
        color: '#7EEAC9',
      },
      {
        top: '-4.6%',
        left: '-5.1%',
        type: 'right',
        delay: '0.4s',
        rotate: '2deg',
        color: '#7EEAC9',
      },
      {
        top: '-7.2%',
        left: '-5.9%',
        type: 'left',
        delay: '0s',
        rotate: '14deg',
        color: '#C6F9E9',
      },
      {
        top: '-7.4%',
        left: '-6.9%',
        type: 'right',
        delay: '0s',
        rotate: '14deg',
        color: '#C6F9E9',
      },
    ],
  },
  mobile: {
    step01: [
      {
        top: '-4%',
        left: '6%',
        type: 'left',
        delay: '0s',
        rotate: '64deg',
        color: '#7EEAC9',
      },
      {
        top: '-4.1%',
        left: '4%',
        type: 'right',
        delay: '0s',
        rotate: '56deg',
        color: '#7EEAC9',
      },
      {
        top: '-6.3%',
        left: '5.4%',
        type: 'left',
        delay: '0.4s',
        rotate: '58deg',
        color: '#C6F9E9',
      },
      {
        top: '-6.4%',
        left: '3.6%',
        type: 'right',
        delay: '0.4s',
        rotate: '50deg',
        color: '#C6F9E9',
      },
    ],
    step02: [
      {
        top: '-3%',
        left: '-2.2%',
        type: 'left',
        delay: '0.8s',
        rotate: '4deg',
        color: '#4DD5A3',
      },
      {
        top: '-3%',
        left: '-4.2%',
        type: 'right',
        delay: '0.8s',
        rotate: '4deg',
        color: '#4DD5A3',
      },
      {
        top: '-5%',
        left: '-4%',
        type: 'left',
        delay: '0.4s',
        rotate: '14deg',
        color: '#7EEAC9',
      },
      {
        top: '-5%',
        left: '-6%',
        type: 'right',
        delay: '0.4s',
        rotate: '14deg',
        color: '#7EEAC9',
      },
      {
        top: '-7.2%',
        left: '-5.2%',
        type: 'left',
        delay: '0s',
        rotate: '50deg',
        color: '#C6F9E9',
      },
      {
        top: '-7.2%',
        left: '-7%',
        type: 'right',
        delay: '0s',
        rotate: '50deg',
        color: '#C6F9E9',
      },
    ],
    step03: [
      {
        top: '-3.5%',
        left: '1.8%',
        type: 'left',
        delay: '0.8s',
        rotate: '10deg',
        color: '#4DD5A3',
      },
      {
        top: '-3.7%',
        left: '-0.6%',
        type: 'right',
        delay: '0.8s',
        rotate: '10deg',
        color: '#4DD5A3',
      },
      {
        top: '-5.6%',
        left: '-1.1%',
        type: 'left',
        delay: '0.4s',
        rotate: '6deg',
        color: '#7EEAC9',
      },
      {
        top: '-5.6%',
        left: '-3.1%',
        type: 'right',
        delay: '0.4s',
        rotate: '6deg',
        color: '#7EEAC9',
      },
    ],
    step04: [
      {
        top: '-2%',
        left: '6.6%',
        type: 'left',
        delay: '0.8s',
        rotate: '100deg',
        color: '#4DD5A3',
      },
      {
        top: '-2.6%',
        left: '5.2%',
        type: 'right',
        delay: '0.8s',
        rotate: '100deg',
        color: '#4DD5A3',
      },
      {
        top: '-4%',
        left: '9%',
        type: 'left',
        delay: '0.4s',
        rotate: '100deg',
        color: '#7EEAC9',
      },
      {
        top: '-4.5%',
        left: '7.6%',
        type: 'right',
        delay: '0.4s',
        rotate: '100deg',
        color: '#7EEAC9',
      },
    ],
    step05: [
      {
        top: '-2.4%',
        left: '0.2%',
        type: 'left',
        delay: '0.8s',
        rotate: '24deg',
        color: '#4DD5A3',
      },
      {
        top: '-2.4%',
        left: '-1.8%',
        type: 'right',
        delay: '0.8s',
        rotate: '14deg',
        color: '#4DD5A3',
      },
      {
        top: '-4.6%',
        left: '-0.6%',
        type: 'left',
        delay: '0.4s',
        rotate: '50deg',
        color: '#7EEAC9',
      },
      {
        top: '-4.6%',
        left: '-2.4%',
        type: 'right',
        delay: '0.4s',
        rotate: '40deg',
        color: '#7EEAC9',
      },
    ],
  },
};

const DASHBOARD_SCENE_SIZES = {
  desktop: { width: 1324, height: 1000 },
  tablet: { width: 984, height: 744 },
  mobile: { width: 382, height: 686 },
};

const IMAGE_SIZE_PERCENT = {
  desktop: {
    building63: 6.83,
    gyeongbokgung: 16.3,
    gwangan: 15.3,
    dolhareubang: 4.53,
    incheon: 10.29,
    parliament: 13.9,
    namsan: 18.86,
    twoTreesA: 4.485,
    twoTreesB: 4.8781,
    twoTreesC: 4.013,
    twoTreesD: 3.9605,
    twoTreesE: 8.2084,
    twoTreesF: 5.01,
    fourTreesA: 6.423,
    fourTreesB: 6.2375,
    fourTreesC: 7.4739,
    oneTreeA: 2.171,
    oneTreeB: 2.319,
    oneTreeC: 2.9086,
    oneTreeD: 2.9874,
    oneTreeE: 3.3426,
    oneTreeF: 2.926,
    oneTreeColor: 5.495,
  },
  tablet: {
    building63: 7.53,
    gyeongbokgung: 18.015,
    gwangan: 16.911,
    dolhareubang: 4.9971,
    incheon: 11.39,
    parliament: 15.3415,
    namsan: 20.804,
    twoTreesA: 5.0445,
    twoTreesB: 5.391,
    twoTreesC: 4.4345,
    twoTreesD: 4.377,
    twoTreesE: null,
    twoTreesF: 5.5361,
    fourTreesA: 7.098,
    fourTreesB: 6.894,
    fourTreesC: 8.259,
    oneTreeA: 2.399,
    oneTreeB: 2.562,
    oneTreeC: 3.214,
    oneTreeD: 3.3004,
    oneTreeE: 3.694,
    oneTreeF: 3.2334,
    oneTreeColor: null,
  },
  mobile: {
    building63: 13.848,
    gyeongbokgung: 28.207,
    gwangan: 28.718,
    dolhareubang: 10,
    incheon: 17.743,
    parliament: 26.155,
    namsan: 33.847,
    twoTreesA: 8.463,
    twoTreesB: 9.232,
    twoTreesC: 8.206,
    twoTreesD: null,
    twoTreesE: null,
    twoTreesF: null,
    twoTreesG: 10.258,
    twoTreesH: 13.59,
    fourTreesA: 13.59,
    fourTreesB: null,
    fourTreesC: 13.59,
    oneTreeA: 5.13,
    oneTreeB: 5.13,
    oneTreeC: 6.668,
    oneTreeD: 6.412,
    oneTreeE: 7,
    oneTreeF: null,
    oneTreeG: 6.412,
    oneTreeH: 5.643,
    oneTreeI: 7.18,
    oneTreeColor: null,
  },
};

const getDashboardSceneKey = (bp) => {
  if (bp === 'desktopL') return 'desktop';
  if (bp === 'desktopS' || bp === 'tabletV') return 'tablet';
  return 'mobile';
};

const getImageSizes = (sceneKey) => {
  const sceneWidth = DASHBOARD_SCENE_SIZES[sceneKey].width;
  const source = IMAGE_SIZE_PERCENT[sceneKey];

  return Object.fromEntries(
    Object.entries(source).map(([key, value]) => [
      key,
      value === null || value === undefined
        ? null
        : `${((value / 100) * sceneWidth).toFixed(2)}px`,
    ]),
  );
};

const STAGE_STEP_MAP = {
  PRE_ENTRY: 'step01',
  ENTRY: 'step02',
  GROWTH: 'step03',
  INTEGRATION: 'step04',
  SETTLEMENT: 'step05',
};

const Dashboard = ({ evaluationData }) => {
  const BASE = `${process.env.REACT_APP_ASSETS_URL}/Kpathport/Dashboard`;
  const imgs = {
    building63: `${BASE}/img-63-building.svg`,
    gyeongbokgung: `${BASE}/img-gyeongbokgung-palace.svg`,
    incheon: `${BASE}/img-incheon-airport.svg`,
    dolhareubang: `${BASE}/img-dolhareubang.svg`,
    gwangan: `${BASE}/img-gwangan-bridge.svg`,
    namsan: `${BASE}/img-namsan-tower-new.svg`,
    parliament: `${BASE}/img-parliament-building.svg`,
    fourTrees: `${BASE}/img-four-trees.svg`,
    twoTrees: `${BASE}/img-two-trees.svg`,
    oneTree: `${BASE}/img-one-tree.svg`,
    oneTreeColor: `${BASE}/img-one-tree-color.svg`,
  };

  const { user } = useAuth();
  const getStepKey = (step) => {
    const parsed = Number(step);
    return Number.isFinite(parsed) && parsed > 0 ? `step0${parsed}` : null;
  };
  const realUserStep =
    STAGE_STEP_MAP[evaluationData?.finalStage] ||
    getStepKey(user?.step) ||
    'step01';
  const userStep = realUserStep;
  const [selectedStep, setSelectedStep] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const bp = useDashboardBreakpoint();
  const sceneKey = getDashboardSceneKey(bp);
  const sceneSize = DASHBOARD_SCENE_SIZES[sceneKey];
  const isSceneDesktop = sceneKey === 'desktop';
  const isSceneTablet = sceneKey === 'tablet';
  const isSceneMobile = sceneKey === 'mobile';

  const v = (mob, tab, desk) =>
    isSceneMobile ? mob : isSceneTablet ? tab : desk;

  const sizes = getImageSizes(sceneKey);
  const currentCircles = STEP_CIRCLES[sceneKey];
  const currentLabels = STEP_LABELS[sceneKey];
  const currentFootprints = FOOTPRINT_DATA[sceneKey];

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setSelectedStep(null);
      setIsClosing(false);
    }, 300);
  };

  // 내부 영역(지도 씬)을 마우스로 드래그하여 스크롤(패닝)할 수 있게 한다.
  const scrollRef = useRef(null);
  const dragRef = useRef({
    isDown: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
    moved: false,
  });

  const handleDragStart = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current = {
      isDown: true,
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: el.scrollLeft,
      scrollTop: el.scrollTop,
      moved: false,
    };
    el.style.cursor = 'grabbing';
  };

  const handleDragMove = (e) => {
    const drag = dragRef.current;
    if (!drag.isDown) return;
    const el = scrollRef.current;
    if (!el) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
      drag.moved = true;
    }
    el.scrollTop = drag.scrollTop - dy;
    el.scrollLeft = drag.scrollLeft - dx;
  };

  const handleDragEnd = () => {
    const el = scrollRef.current;
    dragRef.current.isDown = false;
    if (el) el.style.cursor = 'grab';
  };

  // 드래그(패닝) 후 발생하는 클릭이 스텝 열기/패널 닫기를 트리거하지 않도록 막는다.
  const suppressClickAfterDrag = (e) => {
    if (dragRef.current.moved) {
      e.stopPropagation();
      e.preventDefault();
      dragRef.current.moved = false;
    }
  };

  const formatGoalType = (value) => {
    if (!value) return 'Job Seeker Path';
    const raw = String(value).trim();
    if (!raw) return 'Job Seeker Path';
    const key = raw.replace(/\s+/g, '_').toUpperCase();
    if (key === 'JOB_SEEKER' || key === 'JOB_SEEKER_PATH')
      return 'Job Seeker Path';
    const titled = raw
      .replace(/[_-]+/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return /path/i.test(titled) ? titled : `${titled} Path`;
  };
  const goalTypeLabel = formatGoalType(
    user?.goalTypeLabel ?? user?.goalTypeName ?? user?.goalType ?? user?.goal,
  );

  return (
    <div
      className={`relative w-full rounded-[30px] shadow-popup-01 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden ${
        isSceneDesktop
          ? 'min-w-[548px] h-full'
          : isSceneTablet
            ? 'min-w-[548px] h-full'
            : 'min-w-[328px] h-[686px]'
      }`}
      style={{
        border: '1.6px solid transparent',
        backgroundImage: `linear-gradient(180deg, #FFF0D6 0%, #C6F9E9 100%), linear-gradient(180deg, #ffd181 0%, #c6f9e9 100%)`,
        backgroundOrigin: 'padding-box, border-box',
        backgroundClip: 'padding-box, border-box',
        maxHeight: sceneSize.height,
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
      onClick={() => {
        if (selectedStep) handleClose();
      }}
    >
      <div
        className='relative flex-shrink-0 w-full overflow-hidden'
        style={{ height: sceneSize.height }}
      >
        <div
          className='absolute top-0 left-1/2'
          style={{
            width: `${sceneSize.width}px`,
            height: `${sceneSize.height}px`,
            transform: 'translateX(-50%)',
          }}
        >
          <div
            className={`absolute ${isSceneMobile ? 'top-[18.12px]' : 'top-5'} z-30`}
            style={{ left: '50%', transform: 'translateX(-50%)' }}
          >
            <div
              className='rounded-[50px]'
              style={{
                opacity: 0,
                animation: 'lightOn 0.5s ease-out forwards',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
              }}
            >
              <BaseButton
                type='text'
                size={isSceneMobile ? 'm' : 'xl'}
                bgclass={`${
                  isSceneMobile
                    ? 'rounded-[45.31px] px-[10px] py-2 h-[37px]'
                    : 'rounded-[50px] px-4 py-2 h-[40px]'
                } bg-surface-black60`}
                textclass='text-text-inverse'
                borderclass='border-surface-white15 border-[1.6px]'
                isStatic
                width={isSceneMobile ? '161.12px' : '182px'}
                icon={
                  <GreenCircleIcon
                    style={{
                      filter:
                        'drop-shadow(0 0 6px rgba(29, 195, 123, 0.6)) drop-shadow(0 0 12px rgba(29, 195, 123, 0.5))',
                    }}
                  />
                }
              >
                {goalTypeLabel}
              </BaseButton>
            </div>
          </div>

          <div
            className={`absolute flex items-center justify-center ${
              isSceneMobile
                ? 'inset-x-0 top-0 bottom-0 overflow-hidden'
                : 'inset-0 right-[8%]'
            }`}
          >
            {isSceneTablet ? (
              <DashboardTabletRoute
                style={{ width: 'auto', height: '100%', flexShrink: 0 }}
              />
            ) : isSceneDesktop ? (
              <DashboardRoute
                style={{ width: 'auto', height: '100%', flexShrink: 0 }}
              />
            ) : (
              <DashboardMobileRoute
                style={{
                  width: 'auto',
                  height: '700px',
                  flexShrink: 0,
                  alignSelf: 'flex-start',
                }}
              />
            )}
          </div>

          {currentCircles.map(({ step, top, left }) => {
            const isActive = userStep === step;
            const footprints = currentFootprints[step] || [];
            if (!isActive) return null;
            return footprints.map((fp, i) => (
              <div
                key={`fp-${step}-${i}`}
                className='absolute animate-footprint-pair'
                style={{
                  top: `calc(${top} + ${fp.top})`,
                  left: `calc(${left} + ${fp.left})`,
                  animationDelay: fp.delay,
                  color: fp.color || '#1DC37B',
                }}
              >
                <div
                  style={{
                    transform: `rotate(${fp.rotate || '0deg'}) ${isSceneMobile ? 'scale(0.6)' : ''}`,
                    transformOrigin: 'center',
                  }}
                >
                  {fp.type === 'left' ? <LeftFootprint /> : <RightFootprint />}
                </div>
              </div>
            ));
          })}

          <Landmark
            src={imgs.building63}
            alt='63 building'
            width={sizes.building63}
            pos={{
              right: v('31%', '40%', '44%'),
              bottom: v('2%', '12%', '12%'),
            }}
          />
          <Landmark
            src={imgs.gyeongbokgung}
            alt='gyeongbokgung'
            width={sizes.gyeongbokgung}
            pos={{
              right: v('7%', '18%', '18%'),
              bottom: v('30%', '26%', '32%'),
            }}
          />
          <Landmark
            src={imgs.gwangan}
            alt='gwangan'
            width={sizes.gwangan}
            pos={{ right: v('2%', '13%', '19%'), top: v('28%', '19%', '19%') }}
          />
          <Landmark
            src={imgs.dolhareubang}
            alt='dolhareubang'
            width={sizes.dolhareubang}
            pos={{ left: v('66%', '47%', '47%'), top: v('10%', '15%', '8%') }}
          />
          <Landmark
            src={imgs.incheon}
            alt='incheon airport'
            width={sizes.incheon}
            pos={{ left: v('6%', '16.5%', '18%'), top: v('2%', '0%', '6%') }}
          />
          <Landmark
            src={imgs.parliament}
            alt='parliament'
            width={sizes.parliament}
            pos={{ left: v('5%', '20%', '23%'), top: v('45%', '34%', '42%') }}
          />
          <Landmark
            src={imgs.namsan}
            alt='namsan'
            width={sizes.namsan}
            pos={{
              left: v('-5%', '10%', '12%'),
              bottom: v('-5%', '-8%', '-2%'),
            }}
          />

          <Landmark
            src={imgs.twoTrees}
            alt='twoTrees_incheon_left'
            width={sizes.twoTreesA}
            pos={{
              left: v('-2%', '15%', '17.2%'),
              top: v('18%', '18%', '21%'),
            }}
            imgStyle={{ filter: isSceneMobile ? 'blur(2px)' : undefined }}
          />
          <Landmark
            src={imgs.twoTrees}
            alt='twoTrees_left_route'
            width={sizes.twoTreesB}
            pos={{
              left: v('18.5%', '31%', '36%'),
              top: v('31%', '19%', '15%'),
            }}
          />
          <Landmark
            src={imgs.twoTrees}
            alt='twoTrees_dol_right'
            width={sizes.twoTreesC}
            pos={{
              left: v('84%', '52%', '51.2%'),
              top: v('14%', '15.1%', '7%'),
            }}
            imgStyle={{ filter: isSceneMobile ? 'blur(1.81px)' : 'blur(2px)' }}
          />
          {!isSceneMobile && (
            <Landmark
              src={imgs.twoTrees}
              alt='twoTrees_dol_bottom_right'
              width={sizes.twoTreesC}
              pos={{
                right: isSceneTablet ? '29%' : '39%',
                top: isSceneTablet ? '32%' : '31%',
              }}
              imgStyle={{ filter: 'blur(2px)' }}
            />
          )}
          {!isSceneMobile && (
            <Landmark
              src={imgs.twoTrees}
              alt='twoTrees_bridge_right'
              width={sizes.twoTreesD}
              pos={{
                right: isSceneTablet ? '10%' : '19%',
                top: isSceneTablet ? '20%' : '20%',
              }}
              imgStyle={{ filter: 'blur(2px)' }}
            />
          )}
          {isSceneDesktop && (
            <Landmark
              src={imgs.twoTrees}
              alt='twoTrees_namsan_left'
              width={sizes.twoTreesE}
              pos={{ left: '10%', bottom: '2%' }}
              imgStyle={{ filter: 'blur(5px)' }}
            />
          )}
          {!isSceneMobile && (
            <Landmark
              src={imgs.twoTrees}
              alt='twoTrees_namsan_right'
              width={sizes.twoTreesF}
              pos={{
                left: isSceneTablet ? '32%' : '36%',
                bottom: isSceneTablet ? '-4%' : '3%',
              }}
              imgStyle={{ filter: 'blur(1.09px)' }}
            />
          )}

          <Landmark
            src={imgs.fourTrees}
            alt='fourTrees_namsan_left'
            width={sizes.fourTreesA}
            pos={{
              left: v('-2%', '10%', '13%'),
              bottom: v('28%', '26.8%', '35%'),
            }}
            imgStyle={{ filter: isSceneMobile ? 'blur(1.81px)' : 'blur(2px)' }}
          />
          {!isSceneMobile && (
            <Landmark
              src={imgs.fourTrees}
              alt='fourTrees_63_right'
              width={sizes.fourTreesB}
              pos={{
                right: isSceneTablet ? '34.3%' : '40%',
                bottom: isSceneTablet ? '2%' : '10%',
              }}
            />
          )}
          <Landmark
            src={imgs.fourTrees}
            alt='fourTrees_bottom'
            width={sizes.fourTreesC}
            pos={{
              right: v('70%', '11%', '21%'),
              bottom: v('-4%', '-2.2%', '-20.2%'),
            }}
            imgStyle={{ filter: isSceneMobile ? 'blur(0.9px)' : 'blur(3px)' }}
          />

          <Landmark
            src={imgs.oneTree}
            alt='oneTree_incheon_right'
            width={sizes.oneTreeA}
            pos={{ left: '28%', top: v('-2%', '2%', '5%') }}
            imgStyle={{ filter: 'blur(2px)', opacity: '0.6' }}
          />
          <Landmark
            src={imgs.oneTree}
            alt='oneTree_bridge_top'
            width={sizes.oneTreeB}
            pos={{
              right: v('22%', '30%', '29%'),
              top: v('-1%', '-0.3%', '6%'),
            }}
            imgStyle={{
              filter: isSceneMobile ? 'blur(4.53px)' : 'blur(5px)',
              opacity: '0.6',
            }}
          />
          <Landmark
            src={imgs.oneTree}
            alt='oneTree_gyeongbokgung_top'
            width={sizes.oneTreeC}
            pos={{
              right: v('24%', '29%', '33%'),
              bottom: v('51%', '45.5%', '47%'),
            }}
            imgStyle={{ filter: 'blur(1.09px)' }}
          />
          <Landmark
            src={imgs.oneTree}
            alt='oneTree_gyeongbokgung_right'
            width={`${isSceneMobile ? sizes.oneTreeH : sizes.oneTreeD}`}
            pos={{
              right: v('-2.5%', '12%', '15%'),
              bottom: v('40%', '28%', '36%'),
            }}
            imgStyle={{ filter: 'blur(2px)', opacity: '0.6' }}
          />
          <Landmark
            src={imgs.oneTree}
            alt='oneTree_gyeongbokgung_bottom'
            width={`${isSceneMobile ? sizes.oneTreeI : sizes.oneTreeE}`}
            pos={{
              right: v('16%', '23%', '25%'),
              bottom: v('14%', '22%', '27%'),
            }}
            imgStyle={{ filter: isSceneMobile ? undefined : 'blur(1.09px)' }}
          />
          <Landmark
            src={imgs.oneTree}
            alt='oneTree_parliament_bottom'
            width={sizes.oneTreeD}
            pos={{
              left: v('15%', '25.6%', '30%'),
              bottom: v('38.5%', '45%', '38%'),
            }}
          />
          <Landmark
            src={imgs.oneTree}
            alt='oneTree_namsan_right'
            width={sizes.oneTreeD}
            pos={{
              left: v('20%', '26%', '28.3%'),
              bottom: v('12%', '13%', '15%'),
            }}
            imgStyle={{ filter: isSceneMobile ? 'blur(0.98px)' : undefined }}
          />
          {!isSceneMobile && (
            <Landmark
              src={imgs.oneTree}
              alt='oneTree_63_left'
              width={sizes.oneTreeF}
              pos={{
                left: isSceneTablet ? '41%' : '40%',
                bottom: isSceneTablet ? '27%' : '32.5%',
              }}
              imgStyle={{ filter: 'blur(1.09px)' }}
            />
          )}

          {isSceneMobile && (
            <>
              <Landmark
                src={imgs.oneTree}
                alt='oneTree_dol_bottom_mobile'
                width={sizes.oneTreeG}
                pos={{ right: '36%', top: '24%' }}
                imgStyle={{ filter: 'blur(0.98px)' }}
              />
              <Landmark
                src={imgs.twoTrees}
                alt='twoTrees_route_right'
                width={sizes.twoTreesG}
                pos={{ left: '46%', bottom: '27.6%' }}
                imgStyle={{ filter: 'blur(1.81px)' }}
              />
              <Landmark
                src={imgs.twoTrees}
                alt='twoTrees_bottom_mobile'
                width={sizes.twoTreesH}
                pos={{ right: '1%', bottom: '-3%' }}
                imgStyle={{ filter: 'blur(2px)' }}
              />
            </>
          )}

          {isSceneDesktop && (
            <>
              <Landmark
                src={imgs.oneTreeColor}
                alt='oneTree_bottom_left'
                width={sizes.oneTreeColor}
                pos={{ right: '11.2%', bottom: '-1%' }}
                imgStyle={{ filter: 'blur(7.5px)' }}
              />
            </>
          )}

          {currentCircles.map(({ step, top, left }) => {
            const isActive = userStep === step;
            const labelData = currentLabels.find((l) => l.step === step);

            return (
              <React.Fragment key={step}>
                <div
                  className='absolute flex items-center justify-center'
                  style={{ top, left }}
                >
                  {isActive ? (
                    <div
                      onClick={() => setSelectedStep(step)}
                      className='relative flex items-center justify-center cursor-pointer'
                    >
                      {[0, 1].map((i) => (
                        <span
                          key={i}
                          className='absolute rounded-full bg-green-deep01'
                          style={{
                            width: isSceneMobile ? '42px' : '44px',
                            height: isSceneMobile ? '42px' : '44px',
                            animation: 'circleRipple 1.8s ease-out infinite',
                            animationDelay: i === 1 ? '0.6s' : '0s',
                            opacity: 0,
                          }}
                        />
                      ))}
                      <DashboardCircleActive
                        className={`${
                          isSceneMobile
                            ? 'w-[42px] h-[42px]'
                            : 'w-[44px] h-[44px]'
                        } relative z-10`}
                      />
                    </div>
                  ) : (
                    <DashboardCircle
                      className={
                        isSceneMobile
                          ? 'w-[36px] h-[36px]'
                          : 'w-[38px] h-[38px]'
                      }
                    />
                  )}
                </div>

                {labelData &&
                  (isActive ? (
                    <div
                      onClick={() => setSelectedStep(step)}
                      className={`absolute flex flex-col justify-center items-center cursor-pointer ${
                        isSceneMobile ? 'px-3 py-[10px]' : 'px-3 py-[10px]'
                      } rounded-[16px]  bg-green-deep01 shadow-[0_2px_8px_rgba(29,195,123,0.20),_0_4px_20px_rgba(29,195,123,0.26)]`}
                      style={{
                        top: labelData.activeTop,
                        left: labelData.activeLeft,
                        width: labelData.activeWidth,
                      }}
                    >
                      <div
                        className={`${
                          isSceneMobile ? 'typo-b12' : 'typo-b12Strong'
                        } text-green-deep04 whitespace-nowrap`}
                      >
                        {labelData.stepText}
                      </div>
                      <div
                        className={`${
                          isSceneMobile ? 'typo-b13Strong' : 'typo-b16Strong'
                        } text-functional-success04 whitespace-nowrap`}
                      >
                        {labelData.label}
                      </div>
                    </div>
                  ) : (
                    <div
                      className='absolute flex flex-col justify-center items-center px-3 py-2.5 rounded-[16px]'
                      style={{
                        top: labelData.top,
                        left: labelData.left,
                        width: labelData.width,
                        background: 'rgba(255, 255, 255, 0.70)',
                        backdropFilter: 'blur(30px)',
                        WebkitBackdropFilter: 'blur(30px)',
                      }}
                    >
                      <div
                        className={`${
                          isSceneMobile ? 'typo-b11' : 'typo-b12Strong'
                        } text-text-placeholder whitespace-nowrap`}
                      >
                        {labelData.stepText}
                      </div>
                      <div
                        className={`${
                          isSceneMobile ? 'typo-b13Strong' : 'typo-b14Strong'
                        } text-text-secondary whitespace-nowrap`}
                      >
                        {labelData.label}
                      </div>
                    </div>
                  ))}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <SidePanner
        selectedStep={selectedStep}
        currentLabels={currentLabels}
        userStep={userStep}
        evaluationData={evaluationData}
        isClosing={isClosing}
        handleClose={handleClose}
      />
    </div>
  );
};

export default Dashboard;
